// PreviewTester/utils/parseTemplateForEditFirstLoadOnly.js

import {
  isButtonStep,
  isButtonGroupStep,
  isStyleStep,
  isStructuralStep,
  isMediaStep,
  isCombinedStep,
  hasLoopSelector,
  getSelectorCount
} from './transformationSchema';

export const parseTemplateForEditFirstLoadOnly = (
  templateString,
  parsedSample,
  isCase2 = false,
  linesToUse = null,
  transformationSteps = []
) => {
  console.log('==================================================');
  console.log('[parseTemplateForEditFirstLoadOnly] 📄 Called', JSON.stringify(transformationSteps));
  console.log('[parseTemplateForEditFirstLoadOnly] 📄 isCase2:', isCase2);
  console.log('[parseTemplateForEditFirstLoadOnly] 📄 transformationSteps length:', transformationSteps?.length || 0);
  console.log('[parseTemplateForEditFirstLoadOnly] 📄 templateString length:', templateString?.length || 0);
  console.log('[parseTemplateForEditFirstLoadOnly] 📄 linesToUse:', linesToUse ? `Array(${linesToUse.length})` : 'null');

  if (linesToUse) {
    console.log('[parseTemplateForEditFirstLoadOnly] 📄 linesToUse details:');
    linesToUse.forEach((line, idx) => {
      console.log(`  [${idx}] id: ${line.id}, type: ${line.type}, content: "${line.content?.substring(0, 30)}${line.content?.length > 30 ? '...' : ''}", isCustom: ${line.isCustom}, isButtonGroup: ${line.isButtonGroup}, isButton: ${line.isButton}`);
    });
  }

  if (!templateString) {
    console.log('[parseTemplateForEditFirstLoadOnly] ⚠️ templateString is empty, returning []');
    return [];
  }

  const lines = templateString.split('\n');
  console.log('[parseTemplateForEditFirstLoadOnly] 📄 template lines count:', lines.length);

  const result = [];
  const loopStack = [];
  let currentDepth = 0;
  let inLoop = false;

  // ============================================================
  // ✅ Helper: Get button text safely (handles both string and object)
  // ============================================================
  const getButtonTextSafe = (btn) => {
    if (!btn) return '';
    if (typeof btn === 'string') return btn;
    if (typeof btn === 'object' && btn !== null) {
      return btn.text || btn.label || btn.name || btn.value || String(btn);
    }
    return String(btn);
  };

  // ============================================================
  // ✅ Helper: Get button text for mapping (always returns string)
  // ============================================================
  const getButtonTextForMapping = (btn) => {
    const text = getButtonTextSafe(btn);
    return text ? text.trim() : '';
  };

  // ============================================================
  // ✅ Build a map of transformation steps by content
  // ============================================================
  const stepTypeMap = new Map();
  const stepIndexMap = new Map(); // Map by templateRowIndex for exact position matching

  if (transformationSteps && Array.isArray(transformationSteps)) {
    transformationSteps.forEach((step) => {
      // ============================================================
      // BUTTON GROUP (new minimal schema)
      // ============================================================
      if (isButtonGroupStep(step)) {
        const groupName = step.groupName || '';
        const rowIndex = step._templateRowIndex || -1;

        // Store by content
        if (groupName) {
          stepTypeMap.set(groupName.trim(), {
            type: 'button-group',
            id: step.id,
            buttons: step.buttons || [],
            groupName: groupName,
            templateRowIndex: rowIndex,
          });
        }

        // Store by templateRowIndex for exact position matching
        if (rowIndex !== undefined && rowIndex !== -1) {
          stepIndexMap.set(rowIndex, {
            type: 'button-group',
            id: step.id,
            buttons: step.buttons || [],
            groupName: groupName,
            templateRowIndex: rowIndex,
          });
          console.log(`[parseTemplateForEditFirstLoadOnly] 📌 Mapped button group at index ${rowIndex}: "${groupName}"`);
        }

        // Also map each button's text - SAFELY handle both string and object
        if (step.buttons && Array.isArray(step.buttons)) {
          step.buttons.forEach((btn) => {
            const btnText = getButtonTextForMapping(btn);
            if (btnText) {
              stepTypeMap.set(btnText, {
                type: 'button-group-button',
                groupId: step.id,
                text: btnText,
                groupName: groupName,
                templateRowIndex: rowIndex,
              });
            }
          });
        }
      }

      // ============================================================
      // BUTTON (new minimal schema)
      // ============================================================
      if (isButtonStep(step)) {
        const buttonText = step.buttonText || '';
        const rowIndex = step._templateRowIndex || -1;

        if (buttonText) {
          const trimmedText = buttonText.trim ? buttonText.trim() : String(buttonText).trim();
          stepTypeMap.set(trimmedText, {
            type: 'button',
            id: step.id,
            buttonText: buttonText,
            templateRowIndex: rowIndex,
          });
        }

        // Store by templateRowIndex for exact position matching
        if (rowIndex !== undefined && rowIndex !== -1) {
          stepIndexMap.set(rowIndex, {
            type: 'button',
            id: step.id,
            buttonText: buttonText,
            templateRowIndex: rowIndex,
          });
          console.log(`[parseTemplateForEditFirstLoadOnly] 📌 Mapped button at index ${rowIndex}: "${buttonText}"`);
        }
      }

      // ============================================================
      // STYLE (new minimal schema)
      // ============================================================
      if (isStyleStep(step)) {
        const fields = step.fields || [];
        fields.forEach((field) => {
          if (field) {
            const trimmedField = field.trim ? field.trim() : String(field).trim();
            if (trimmedField) {
              stepTypeMap.set(trimmedField, {
                type: 'style',
                id: step.id,
                field: field,
                operation: step.operation,
              });
            }
          }
        });
      }

      // ============================================================
      // STRUCTURAL (new minimal schema)
      // ============================================================
      if (isStructuralStep(step)) {
        const fields = step.fields || [];
        fields.forEach((field) => {
          if (field) {
            const trimmedField = field.trim ? field.trim() : String(field).trim();
            if (trimmedField) {
              stepTypeMap.set(trimmedField, {
                type: 'structural',
                id: step.id,
                field: field,
                operation: step.operation,
              });
            }
          }
        });
      }

      // ============================================================
      // MEDIA (new minimal schema)
      // ============================================================
      if (isMediaStep(step)) {
        const fields = step.fields || [];
        fields.forEach((field) => {
          if (field) {
            const trimmedField = field.trim ? field.trim() : String(field).trim();
            if (trimmedField) {
              stepTypeMap.set(trimmedField, {
                type: 'media',
                id: step.id,
                field: field,
                operation: step.operation,
              });
            }
          }
        });
      }

      // ============================================================
      // COMBINED (new minimal schema)
      // ============================================================
      if (isCombinedStep(step)) {
        // Map structural operations
        if (step.phases?.structural?.operations) {
          step.phases.structural.operations.forEach((op) => {
            if (op.fields) {
              op.fields.forEach((field) => {
                if (field) {
                  const trimmedField = field.trim ? field.trim() : String(field).trim();
                  if (trimmedField) {
                    stepTypeMap.set(trimmedField, {
                      type: 'combined-structural',
                      id: step.id,
                      field: field,
                      operation: op.operation,
                    });
                  }
                }
              });
            }
          });
        }

        // Map media operations
        if (step.phases?.media?.operations) {
          step.phases.media.operations.forEach((op) => {
            if (op.fields) {
              op.fields.forEach((field) => {
                if (field) {
                  const trimmedField = field.trim ? field.trim() : String(field).trim();
                  if (trimmedField) {
                    stepTypeMap.set(trimmedField, {
                      type: 'combined-media',
                      id: step.id,
                      field: field,
                      operation: op.operation,
                    });
                  }
                }
              });
            }
          });
        }

        // Map styles operations
        if (step.phases?.styles?.operations) {
          step.phases.styles.operations.forEach((op) => {
            if (op.fields) {
              op.fields.forEach((field) => {
                if (field) {
                  const trimmedField = field.trim ? field.trim() : String(field).trim();
                  if (trimmedField) {
                    stepTypeMap.set(trimmedField, {
                      type: 'combined-styles',
                      id: step.id,
                      field: field,
                      operation: op.operation,
                    });
                  }
                }
              });
            }
          });
        }
      }
    });
  }

  console.log('[parseTemplateForEditFirstLoadOnly] 📊 stepTypeMap entries:', Array.from(stepTypeMap.keys()));
  console.log('[parseTemplateForEditFirstLoadOnly] 📊 stepIndexMap entries:', Array.from(stepIndexMap.keys()));

  // ============================================================
  // Store ALL lines by content for lookup
  // ============================================================
  const existingLinesByContent = new Map();

  if (linesToUse) {
    linesToUse.forEach((line, idx) => {
      const trimmed = (line.content || '').trim ? (line.content || '').trim() : String(line.content || '').trim();
      if (trimmed) {
        if (!existingLinesByContent.has(trimmed)) {
          existingLinesByContent.set(trimmed, []);
        }
        existingLinesByContent.get(trimmed).push({ line, index: idx });
      }
    });
  }

  console.log('[parseTemplateForEditFirstLoadOnly] 📄 existingLinesByContent entries:');
  for (const [content, entries] of existingLinesByContent) {
    console.log(`  "${content}" -> ${entries.length} entries:`, entries.map(e => `${e.line.type}(${e.index})`).join(', '));
  }

  // ============================================================
  // Helper: Find existing line by content
  // ============================================================
  const findExistingLine = (lineContent, currentIndex) => {
    const trimmed = lineContent.trim ? lineContent.trim() : String(lineContent).trim();
    if (!trimmed) return null;

    const entries = existingLinesByContent.get(trimmed);
    if (!entries || entries.length === 0) {
      console.log(`[parseTemplateForEditFirstLoadOnly] ⚠️ No entries found for: "${trimmed}"`);
      return null;
    }

    if (entries.length === 1) {
      console.log(`[parseTemplateForEditFirstLoadOnly] ✅ Found single entry for "${trimmed}" -> type: ${entries[0].line.type}`);
      return entries[0].line;
    }

    console.log(`[parseTemplateForEditFirstLoadOnly] 🔍 Multiple entries (${entries.length}) for "${trimmed}"`);

    // ✅ FIRST: Check if there's a transformation step at this exact index
    const stepInfo = stepIndexMap.get(currentIndex);
    if (stepInfo) {
      console.log(`[parseTemplateForEditFirstLoadOnly] ✅ Found step at index ${currentIndex} -> type: ${stepInfo.type}`);
      // Return an existing line if it matches, or create a new one
      const matchingEntry = entries.find(e => {
        if (stepInfo.type === 'button-group') {
          return e.line.type === 'button-group' || e.line.isButtonGroup === true;
        }
        if (stepInfo.type === 'button') {
          return e.line.type === 'button' || e.line.isButton === true;
        }
        return false;
      });
      if (matchingEntry) {
        console.log(`[parseTemplateForEditFirstLoadOnly] ✅ Found matching entry at index ${matchingEntry.index}`);
        return matchingEntry.line;
      }
      // If no matching entry, we'll handle this in the main loop
      return null;
    }

    // ✅ SECOND: Try to match by index
    const indexMatch = entries.find(e => e.index === currentIndex);
    if (indexMatch) {
      console.log(`[parseTemplateForEditFirstLoadOnly] ✅ Found exact index match at ${currentIndex} -> type: ${indexMatch.line.type}`);
      return indexMatch.line;
    }

    // Priority 1: Button group (highest priority for "Button Group" content)
    const buttonGroupEntry = entries.find(e => e.line.type === 'button-group' || e.line.isButtonGroup === true);
    if (buttonGroupEntry) {
      console.log(`[parseTemplateForEditFirstLoadOnly] ✅ Found button group entry -> using it`);
      return buttonGroupEntry.line;
    }

    // Priority 2: Custom message
    const customEntry = entries.find(e => e.line.type === 'custom' || e.line.isCustom === true);
    if (customEntry) {
      console.log(`[parseTemplateForEditFirstLoadOnly] ✅ Found custom message entry -> using it`);
      return customEntry.line;
    }

    // Priority 3: Button
    const buttonEntry = entries.find(e => e.line.type === 'button' || e.line.isButton === true);
    if (buttonEntry) {
      console.log(`[parseTemplateForEditFirstLoadOnly] ✅ Found button entry -> using it`);
      return buttonEntry.line;
    }

    // Priority 4: Use the one closest to current index
    const closestEntry = entries.reduce((closest, current) => {
      const currentDist = Math.abs(current.index - currentIndex);
      const closestDist = Math.abs(closest.index - currentIndex);
      return currentDist < closestDist ? current : closest;
    });
    console.log(`[parseTemplateForEditFirstLoadOnly] ✅ Using closest entry (index ${closestEntry.index}) -> type: ${closestEntry.line.type}`);
    return closestEntry.line;
  };

  // ============================================================
  // Helper: Extract variables from content
  // ============================================================
  const extractVariables = (content) => {
    const variables = [];
    const varRegex = /{{[^}]+}}/g;
    let match;
    while ((match = varRegex.exec(content)) !== null) {
      const varName = match[0].replace(/[{}]/g, '').trim();
      if (varName && !variables.includes(varName)) {
        variables.push(varName);
      }
    }
    return variables;
  };

  // ============================================================
  // Helper: Check if a line is a custom message
  // ============================================================
  const isCustomMessage = (existingLine) => {
    if (!existingLine) return false;

    // ✅ CHECK TYPE FIRST - button is NOT a custom message
    if (existingLine.type === 'button') {
      console.log('[parseTemplateForEditFirstLoadOnly] 🔍 isCustomMessage: type === "button" -> false (button)');
      return false;
    }

    // ✅ CHECK TYPE FIRST - button-group is NOT a custom message
    if (existingLine.type === 'button-group') {
      console.log('[parseTemplateForEditFirstLoadOnly] 🔍 isCustomMessage: type === "button-group" -> false (button group)');
      return false;
    }

    // Then check for custom
    if (existingLine.type === 'custom') {
      console.log('[parseTemplateForEditFirstLoadOnly] 🔍 isCustomMessage: type === "custom" -> true');
      return true;
    }
    if (existingLine.isCustom === true) {
      console.log('[parseTemplateForEditFirstLoadOnly] 🔍 isCustomMessage: isCustom === true -> true');
      return true;
    }
    if (existingLine.customId) {
      console.log('[parseTemplateForEditFirstLoadOnly] 🔍 isCustomMessage: has customId -> true');
      return true;
    }

    console.log('[parseTemplateForEditFirstLoadOnly] 🔍 isCustomMessage: false');
    return false;
  };

  // ============================================================
  // Helper: Check if a line is a button group - FIXED PRIORITY
  // ============================================================
  const isButtonGroupLine = (trimmed, existingLine, currentIndex) => {
    if (!trimmed) return false;

    // ✅ FIRST: Check if there's a transformation step at this exact index
    const stepInfo = stepIndexMap.get(currentIndex);
    if (stepInfo && stepInfo.type === 'button-group') {
      console.log(`[parseTemplateForEditFirstLoadOnly] 🔍 isButtonGroupLine: at index ${currentIndex} is button group from step -> true`);
      return true;
    }
    // If the step at this index is a button, it's NOT a button group
    if (stepInfo && stepInfo.type === 'button') {
      console.log(`[parseTemplateForEditFirstLoadOnly] 🔍 isButtonGroupLine: at index ${currentIndex} is a button, NOT a button group`);
      return false;
    }

    // ✅ SECOND: Check transformation steps by content (only if no index match)
    const contentStep = stepTypeMap.get(trimmed);
    if (contentStep && contentStep.type === 'button-group') {
      console.log(`[parseTemplateForEditFirstLoadOnly] 🔍 isButtonGroupLine: "${trimmed}" is a button group from transformation steps -> true`);
      return true;
    }

    // ✅ THIRD: Check if it was explicitly a button group
    if (existingLine) {
      if (existingLine.type === 'button-group') {
        console.log(`[parseTemplateForEditFirstLoadOnly] 🔍 isButtonGroupLine: "${trimmed}" has type === "button-group" -> true`);
        return true;
      }
      if (existingLine.isButtonGroup === true) {
        console.log(`[parseTemplateForEditFirstLoadOnly] 🔍 isButtonGroupLine: "${trimmed}" has isButtonGroup === true -> true`);
        return true;
      }
    }

    // ✅ FOURTH: Check if it was a custom message - if so, NOT a button group
    if (isCustomMessage(existingLine)) {
      console.log(`[parseTemplateForEditFirstLoadOnly] 🔍 isButtonGroupLine: "${trimmed}" is a custom message, NOT a button group`);
      return false;
    }

    // ✅ FIFTH: Check content patterns
    if (trimmed.startsWith('[Button Group]')) {
      console.log(`[parseTemplateForEditFirstLoadOnly] 🔍 isButtonGroupLine: "${trimmed}" matches [Button Group] pattern -> true`);
      return true;
    }

    const exactButtonGroupMatch = trimmed.match(/^Button Group\s*(\d*)$/);
    if (exactButtonGroupMatch) {
      console.log(`[parseTemplateForEditFirstLoadOnly] 🔍 isButtonGroupLine: "${trimmed}" is exactly "Button Group" with optional number -> true`);
      return true;
    }

    console.log(`[parseTemplateForEditFirstLoadOnly] 🔍 isButtonGroupLine: "${trimmed}" -> false`);
    return false;
  };

  // ============================================================
  // Helper: Check if a line is a button (single) - FIXED PRIORITY
  // ============================================================
  const isButtonLine = (trimmed, existingLine, currentIndex) => {
    if (!trimmed) return false;

    // ✅ FIRST: If the existing line explicitly says it's NOT a button, respect that
    if (existingLine && existingLine.isButton === false) {
      console.log(`[parseTemplateForEditFirstLoadOnly] 🔍 isButtonLine: "${trimmed}" is explicitly NOT a button -> false`);
      return false;
    }

    // ✅ SECOND: Check if there's a transformation step at this exact index
    const stepInfo = stepIndexMap.get(currentIndex);
    // If the step at this index is a button-group, it's NOT a button
    if (stepInfo && stepInfo.type === 'button-group') {
      console.log(`[parseTemplateForEditFirstLoadOnly] 🔍 isButtonLine: at index ${currentIndex} is a button-group, NOT a button`);
      return false;
    }
    if (stepInfo && stepInfo.type === 'button') {
      console.log(`[parseTemplateForEditFirstLoadOnly] 🔍 isButtonLine: at index ${currentIndex} is button from step -> true`);
      return true;
    }

    // ✅ THIRD: Check transformation steps by content (only if no index match)
    const contentStep = stepTypeMap.get(trimmed);
    if (contentStep && contentStep.type === 'button') {
      console.log(`[parseTemplateForEditFirstLoadOnly] 🔍 isButtonLine: "${trimmed}" is a button from transformation steps -> true`);
      return true;
    }
    if (contentStep && contentStep.type === 'button-group-button') {
      console.log(`[parseTemplateForEditFirstLoadOnly] 🔍 isButtonLine: "${trimmed}" is a button group button -> true`);
      return true;
    }

    // ✅ FOURTH: Check existing line
    if (existingLine && existingLine.isButton === true) {
      return true;
    }
    if (existingLine && existingLine.type === 'button') {
      return true;
    }

    // ✅ FIFTH: Check if it's a custom message - if so, NOT a button
    if (isCustomMessage(existingLine) && !existingLine?.isButton) {
      return false;
    }

    return false;
  };

  // ============================================================
  // Helper: Check if a line is a style transformation
  // ============================================================
  const isStyleLine = (trimmed, existingLine, currentIndex) => {
    if (!trimmed) return false;
    
    // Check if there's a style step at this index
    const stepInfo = stepIndexMap.get(currentIndex);
    if (stepInfo && stepInfo.type === 'style') {
      return true;
    }
    
    // Check by content
    const contentStep = stepTypeMap.get(trimmed);
    if (contentStep && (contentStep.type === 'style' || contentStep.type === 'combined-styles')) {
      return true;
    }
    
    return false;
  };

  // ============================================================
  // Helper: Check if a line is a structural transformation
  // ============================================================
  const isStructuralLine = (trimmed, existingLine, currentIndex) => {
    if (!trimmed) return false;
    
    // Check if there's a structural step at this index
    const stepInfo = stepIndexMap.get(currentIndex);
    if (stepInfo && stepInfo.type === 'structural') {
      return true;
    }
    
    // Check by content
    const contentStep = stepTypeMap.get(trimmed);
    if (contentStep && (contentStep.type === 'structural' || contentStep.type === 'combined-structural')) {
      return true;
    }
    
    return false;
  };

  // ============================================================
  // Helper: Check if a line is a media transformation
  // ============================================================
  const isMediaLine = (trimmed, existingLine, currentIndex) => {
    if (!trimmed) return false;
    
    // Check if there's a media step at this index
    const stepInfo = stepIndexMap.get(currentIndex);
    if (stepInfo && stepInfo.type === 'media') {
      return true;
    }
    
    // Check by content
    const contentStep = stepTypeMap.get(trimmed);
    if (contentStep && (contentStep.type === 'media' || contentStep.type === 'combined-media')) {
      return true;
    }
    
    return false;
  };

  // ============================================================
  // MAIN PARSING LOOP
  // ============================================================
  console.log('[parseTemplateForEditFirstLoadOnly] 📄 Starting main parsing loop...');

  for (let i = 0; i < lines.length; i++) {
    const originalLine = lines[i];
    const trimmed = originalLine.trim ? originalLine.trim() : String(originalLine).trim();

    console.log(`[parseTemplateForEditFirstLoadOnly] 📄 --- Line ${i} ---`);
    console.log(`[parseTemplateForEditFirstLoadOnly] 📄 originalLine: "${originalLine}"`);
    console.log(`[parseTemplateForEditFirstLoadOnly] 📄 trimmed: "${trimmed}"`);

    const existingLine = findExistingLine(originalLine, i);

    console.log(`[parseTemplateForEditFirstLoadOnly] 📄 existingLine found:`, existingLine ? {
      id: existingLine.id,
      type: existingLine.type,
      isCustom: existingLine.isCustom,
      isButtonGroup: existingLine.isButtonGroup,
      isButton: existingLine.isButton,
      customId: existingLine.customId,
      content: existingLine.content?.substring(0, 30) + (existingLine.content?.length > 30 ? '...' : '')
    } : 'null');

    // ============================================================
    // STEP 1: Check for Button Group - HIGHEST PRIORITY (must check before button)
    // ============================================================
    if (isButtonGroupLine(trimmed, existingLine, i)) {
      console.log(`[parseTemplateForEditFirstLoadOnly] 📄 Line ${i}: "${trimmed}" is a BUTTON GROUP`);

      const stepInfo = stepTypeMap.get(trimmed) || stepIndexMap.get(i);
      const buttonsFromStep = stepInfo && stepInfo.type === 'button-group' ? stepInfo.buttons : [];

      if (existingLine && existingLine.type === 'button-group') {
        console.log(`[parseTemplateForEditFirstLoadOnly] ✅ Preserving existing button group with id: ${existingLine.id}`);
        result.push({
          ...existingLine,
          id: existingLine.id || `button-group-${Date.now()}`,
          content: originalLine,
          trimmed: trimmed,
          indent: originalLine.match(/^(\s*)/)?.[1] || '',
          depth: currentDepth,
          type: 'button-group',
          isButtonGroup: true,
          isCustom: false,
          buttons: existingLine.buttons && existingLine.buttons.length > 0 ? existingLine.buttons : buttonsFromStep.map((b) => {
            // ✅ SAFELY get button text - handles both string and object
            const text = getButtonTextSafe(b);
            return {
              id: `btn-${Date.now()}-${Math.random().toString(36).substr(2, 4)}`,
              text: text,
              index: 0
            };
          }),
          _sourceType: 'button-group',
          _stepInfo: stepInfo || null,
          _templateRowIndex: stepInfo?.templateRowIndex !== undefined ? stepInfo.templateRowIndex : -1,
        });
        continue;
      }

      // Otherwise create a new button group
      console.log(`[parseTemplateForEditFirstLoadOnly] 🔧 Creating new button group from content: "${trimmed}"`);

      let buttonObjects = [];
      if (buttonsFromStep && buttonsFromStep.length > 0) {
        buttonObjects = buttonsFromStep.map((btn, idx) => {
          // ✅ SAFELY get button text - handles both string and object
          const text = getButtonTextSafe(btn);
          return {
            id: `btn-${Date.now()}-${idx}`,
            text: text,
            index: idx,
          };
        });
      } else {
        // Fallback: parse from content
        const buttonGroupMatch = trimmed.match(/^\[Button Group\]\s*(.+)$/);
        let buttonNames = [];

        if (buttonGroupMatch) {
          buttonNames = buttonGroupMatch[1].split(',').map(b => b.trim()).filter(b => b);
        } else {
          const nameMatch = trimmed.match(/^Button Group\s*(.+)$/);
          if (nameMatch) {
            const name = nameMatch[1].trim();
            if (name && !isNaN(name)) {
              buttonNames = [`Button ${name}`];
            } else {
              buttonNames = name.split(',').map(b => b.trim()).filter(b => b);
              if (buttonNames.length === 0) {
                buttonNames = ['Button 1'];
              }
            }
          } else {
            buttonNames = ['Button 1'];
          }
        }

        buttonObjects = buttonNames.map((text, idx) => ({
          id: `btn-${Date.now()}-${idx}`,
          text: text,
          index: idx,
        }));
      }

      result.push({
        id: `button-group-${Date.now()}`,
        content: originalLine,
        trimmed: trimmed,
        type: 'button-group',
        indent: originalLine.match(/^(\s*)/)?.[1] || '',
        isLoopStart: false,
        isLoopEnd: false,
        loopVariable: null,
        loopArray: null,
        condition: null,
        variables: [],
        isInForLoop: currentDepth > 0 && inLoop,
        isInIfBlock: false,
        depth: currentDepth,
        originalIndex: -1,
        isCustom: false,
        isButtonGroup: true,
        customId: null,
        customMessage: null,
        buttons: buttonObjects,
        needsTransformationStep: true,
        isButton: false,
        _sourceType: 'button-group',
        _stepInfo: stepInfo || null,
        _templateRowIndex: stepInfo?.templateRowIndex !== undefined ? stepInfo.templateRowIndex : -1,
      });
      console.log(`[parseTemplateForEditFirstLoadOnly] ✅ Added as BUTTON GROUP with ${buttonObjects.length} buttons`);
      continue;
    }

    // ============================================================
    // STEP 2: Check for Button (single)
    // ============================================================
    if (isButtonLine(trimmed, existingLine, i)) {
      console.log(`[parseTemplateForEditFirstLoadOnly] 📄 Line ${i}: "${trimmed}" is a BUTTON`);

      const stepInfo = stepTypeMap.get(trimmed) || stepIndexMap.get(i);
      const isButtonGroupButton = stepInfo && stepInfo.type === 'button-group-button';

      result.push({
        id: existingLine?.id || `button-${Date.now()}`,
        content: originalLine,
        trimmed: trimmed,
        type: 'button',
        indent: originalLine.match(/^(\s*)/)?.[1] || '',
        isLoopStart: false,
        isLoopEnd: false,
        loopVariable: null,
        loopArray: null,
        condition: null,
        variables: existingLine?.variables || extractVariables(originalLine),
        isInForLoop: currentDepth > 0 && inLoop,
        isInIfBlock: false,
        depth: currentDepth,
        originalIndex: -1,
        isCustom: true,
        isButton: true,
        isButtonGroup: false,
        isButtonGroupButton: isButtonGroupButton,
        customId: existingLine?.customId || `button-${Date.now()}`,
        customMessage: existingLine?.customMessage || trimmed,
        buttonText: existingLine?.buttonText || trimmed,
        needsTransformationStep: true,
        _sourceType: 'button',
        _stepInfo: stepInfo || null,
      });
      console.log(`[parseTemplateForEditFirstLoadOnly] ✅ Added as BUTTON: "${trimmed}"`);
      continue;
    }

    // ============================================================
    // STEP 3: Check for Custom Message
    // ============================================================
    if (isCustomMessage(existingLine)) {
      console.log(`[parseTemplateForEditFirstLoadOnly] ✅ Line ${i}: "${trimmed}" is a CUSTOM MESSAGE (preserving type)`);

      result.push({
        id: existingLine?.id || `custom-${Date.now()}-${i}`,
        content: originalLine,
        trimmed: trimmed,
        type: 'custom',
        indent: originalLine.match(/^(\s*)/)?.[1] || '',
        isLoopStart: false,
        isLoopEnd: false,
        loopVariable: null,
        loopArray: null,
        condition: null,
        variables: existingLine?.variables || extractVariables(originalLine),
        isInForLoop: currentDepth > 0 && inLoop,
        isInIfBlock: false,
        depth: currentDepth,
        originalIndex: -1,
        isCustom: true,
        customId: existingLine?.customId || `custom-${Date.now()}-${i}`,
        customMessage: existingLine?.customMessage || trimmed,
        isButton: false,
        isButtonGroup: false,
        buttons: null,
        needsTransformationStep: false,
        _sourceType: 'custom',
      });
      console.log(`[parseTemplateForEditFirstLoadOnly] ✅ Added as CUSTOM: "${trimmed}"`);
      continue;
    }

    // ============================================================
    // STEP 4: Handle template tags (for, if, endif, endfor, etc.)
    // ============================================================

    // Check for FOR loop start
    const forMatch = trimmed.match(/{%\s*for\s+([a-zA-Z_][a-zA-Z0-9_]*)\s+in\s+([^%]+)\s*%}/);
    if (forMatch) {
      const loopVar = forMatch[1];
      const loopArray = forMatch[2].trim();

      console.log(`[parseTemplateForEditFirstLoadOnly] 📄 Line ${i}: FOR LOOP START (${loopVar})`);

      loopStack.push({ varName: loopVar, depth: currentDepth });
      currentDepth++;
      inLoop = true;

      result.push({
        id: `for-start-${i}`,
        content: originalLine,
        trimmed: trimmed,
        type: 'for_loop_start',
        indent: originalLine.match(/^(\s*)/)?.[1] || '',
        isLoopStart: true,
        isLoopEnd: false,
        loopVariable: loopVar,
        loopArray: loopArray,
        condition: null,
        variables: [],
        isInForLoop: currentDepth > 1 && inLoop,
        isInIfBlock: false,
        depth: currentDepth - 1,
        originalIndex: -1,
        isCustom: false,
        isButton: false,
        isButtonGroup: false,
        _sourceType: 'loop_start',
      });
      continue;
    }

    // Check for FOR loop end
    const endForMatch = trimmed.match(/{%\s*endfor\s*%}/);
    if (endForMatch && loopStack.length > 0) {
      const loopInfo = loopStack.pop();
      currentDepth--;
      if (loopStack.length === 0) {
        inLoop = false;
      }

      console.log(`[parseTemplateForEditFirstLoadOnly] 📄 Line ${i}: FOR LOOP END`);

      result.push({
        id: `for-end-${i}`,
        content: originalLine,
        trimmed: trimmed,
        type: 'for_loop_end',
        indent: originalLine.match(/^(\s*)/)?.[1] || '',
        isLoopStart: false,
        isLoopEnd: true,
        loopVariable: loopInfo?.varName || null,
        loopArray: null,
        condition: null,
        variables: [],
        isInForLoop: inLoop,
        isInIfBlock: false,
        depth: currentDepth,
        originalIndex: -1,
        isCustom: false,
        isButton: false,
        isButtonGroup: false,
        _sourceType: 'loop_end',
      });
      continue;
    }

    // ============================================================
    // STEP 5: Handle IF conditions
    // ============================================================
    const ifMatch = trimmed.match(/{%\s*if\s+([^%]+)\s*%}/);
    if (ifMatch) {
      const condition = ifMatch[1].trim();
      console.log(`[parseTemplateForEditFirstLoadOnly] 📄 Line ${i}: IF START: ${condition}`);
      result.push({
        id: `if-start-${i}`,
        content: originalLine,
        trimmed: trimmed,
        type: 'if_start',
        indent: originalLine.match(/^(\s*)/)?.[1] || '',
        isLoopStart: false,
        isLoopEnd: false,
        loopVariable: null,
        loopArray: null,
        condition: condition,
        variables: extractVariables(condition),
        isInForLoop: currentDepth > 0 && inLoop,
        isInIfBlock: true,
        depth: currentDepth,
        originalIndex: -1,
        isCustom: false,
        isButton: false,
        isButtonGroup: false,
        _sourceType: 'if_start',
      });
      continue;
    }

    const elifMatch = trimmed.match(/{%\s*elif\s+([^%]+)\s*%}/);
    if (elifMatch) {
      const condition = elifMatch[1].trim();
      console.log(`[parseTemplateForEditFirstLoadOnly] 📄 Line ${i}: ELIF: ${condition}`);
      result.push({
        id: `elif-${i}`,
        content: originalLine,
        trimmed: trimmed,
        type: 'elif',
        indent: originalLine.match(/^(\s*)/)?.[1] || '',
        isLoopStart: false,
        isLoopEnd: false,
        loopVariable: null,
        loopArray: null,
        condition: condition,
        variables: extractVariables(condition),
        isInForLoop: currentDepth > 0 && inLoop,
        isInIfBlock: true,
        depth: currentDepth,
        originalIndex: -1,
        isCustom: false,
        isButton: false,
        isButtonGroup: false,
        _sourceType: 'elif',
      });
      continue;
    }

    const elseMatch = trimmed.match(/{%\s*else\s*%}/);
    if (elseMatch) {
      console.log(`[parseTemplateForEditFirstLoadOnly] 📄 Line ${i}: ELSE`);
      result.push({
        id: `else-${i}`,
        content: originalLine,
        trimmed: trimmed,
        type: 'else',
        indent: originalLine.match(/^(\s*)/)?.[1] || '',
        isLoopStart: false,
        isLoopEnd: false,
        loopVariable: null,
        loopArray: null,
        condition: null,
        variables: [],
        isInForLoop: currentDepth > 0 && inLoop,
        isInIfBlock: true,
        depth: currentDepth,
        originalIndex: -1,
        isCustom: false,
        isButton: false,
        isButtonGroup: false,
        _sourceType: 'else',
      });
      continue;
    }

    const endifMatch = trimmed.match(/{%\s*endif\s*%}/);
    if (endifMatch) {
      console.log(`[parseTemplateForEditFirstLoadOnly] 📄 Line ${i}: ENDIF`);
      result.push({
        id: `endif-${i}`,
        content: originalLine,
        trimmed: trimmed,
        type: 'if_end',
        indent: originalLine.match(/^(\s*)/)?.[1] || '',
        isLoopStart: false,
        isLoopEnd: false,
        loopVariable: null,
        loopArray: null,
        condition: null,
        variables: [],
        isInForLoop: currentDepth > 0 && inLoop,
        isInIfBlock: false,
        depth: currentDepth,
        originalIndex: -1,
        isCustom: false,
        isButton: false,
        isButtonGroup: false,
        _sourceType: 'if_end',
      });
      continue;
    }

    // ============================================================
    // STEP 6: Handle comments and empty lines
    // ============================================================
    if (trimmed.startsWith('{#') || trimmed.startsWith('{% comment')) {
      console.log(`[parseTemplateForEditFirstLoadOnly] 📄 Line ${i}: COMMENT`);
      result.push({
        id: `comment-${i}`,
        content: originalLine,
        trimmed: trimmed,
        type: 'comment',
        indent: originalLine.match(/^(\s*)/)?.[1] || '',
        isLoopStart: false,
        isLoopEnd: false,
        loopVariable: null,
        loopArray: null,
        condition: null,
        variables: [],
        isInForLoop: currentDepth > 0 && inLoop,
        isInIfBlock: false,
        depth: currentDepth,
        originalIndex: -1,
        isCustom: false,
        isButton: false,
        isButtonGroup: false,
        _sourceType: 'comment',
      });
      continue;
    }

    if (!trimmed) {
      console.log(`[parseTemplateForEditFirstLoadOnly] 📄 Line ${i}: EMPTY`);
      result.push({
        id: `empty-${i}`,
        content: originalLine,
        trimmed: trimmed,
        type: 'empty',
        indent: originalLine.match(/^(\s*)/)?.[1] || '',
        isLoopStart: false,
        isLoopEnd: false,
        loopVariable: null,
        loopArray: null,
        condition: null,
        variables: [],
        isInForLoop: currentDepth > 0 && inLoop,
        isInIfBlock: false,
        depth: currentDepth,
        originalIndex: -1,
        isCustom: false,
        isButton: false,
        isButtonGroup: false,
        _sourceType: 'empty',
      });
      continue;
    }

    // ============================================================
    // STEP 7: Handle regular text content
    // ============================================================
    // Check if it's a section header
    if (/^[A-Za-z\s]+:$/.test(trimmed)) {
      console.log(`[parseTemplateForEditFirstLoadOnly] 📄 Line ${i}: SECTION HEADER: "${trimmed}"`);
      result.push({
        id: `text-${i}`,
        content: originalLine,
        trimmed: trimmed,
        type: 'text',
        indent: originalLine.match(/^(\s*)/)?.[1] || '',
        isLoopStart: false,
        isLoopEnd: false,
        loopVariable: null,
        loopArray: null,
        condition: null,
        variables: extractVariables(originalLine),
        isInForLoop: currentDepth > 0 && inLoop,
        isInIfBlock: false,
        depth: currentDepth,
        originalIndex: -1,
        isCustom: false,
        isButton: false,
        isButtonGroup: false,
        _sourceType: 'text',
      });
      continue;
    }

    // Check if it's a key-value pair
    const kvMatch = trimmed.match(/^([a-zA-Z_][a-zA-Z0-9_]*)\s*[-:=>]\s*(.+)$/);
    if (kvMatch) {
      console.log(`[parseTemplateForEditFirstLoadOnly] 📄 Line ${i}: KEY-VALUE: "${trimmed}"`);
      result.push({
        id: `text-${i}`,
        content: originalLine,
        trimmed: trimmed,
        type: 'text',
        indent: originalLine.match(/^(\s*)/)?.[1] || '',
        isLoopStart: false,
        isLoopEnd: false,
        loopVariable: null,
        loopArray: null,
        condition: null,
        variables: extractVariables(originalLine),
        isInForLoop: currentDepth > 0 && inLoop,
        isInIfBlock: false,
        depth: currentDepth,
        originalIndex: -1,
        isCustom: false,
        isButton: false,
        isButtonGroup: false,
        _sourceType: 'text',
      });
      continue;
    }

    // Default: treat as plain text
    console.log(`[parseTemplateForEditFirstLoadOnly] 📄 Line ${i}: PLAIN TEXT: "${trimmed}"`);
    result.push({
      id: `text-${i}`,
      content: originalLine,
      trimmed: trimmed,
      type: 'text',
      indent: originalLine.match(/^(\s*)/)?.[1] || '',
      isLoopStart: false,
      isLoopEnd: false,
      loopVariable: null,
      loopArray: null,
      condition: null,
      variables: extractVariables(originalLine),
      isInForLoop: currentDepth > 0 && inLoop,
      isInIfBlock: false,
      depth: currentDepth,
      originalIndex: -1,
      isCustom: false,
      isButton: false,
      isButtonGroup: false,
      _sourceType: 'text',
    });
  }

  console.log('[parseTemplateForEditFirstLoadOnly] 📄 ===== FINAL PARSED LINES =====');
  console.log('[parseTemplateForEditFirstLoadOnly] 📄 Total lines:', result.length);
  result.forEach((line, idx) => {
    console.log(`[parseTemplateForEditFirstLoadOnly] 📄   [${idx}] type: ${line.type}, content: "${line.content?.substring(0, 40)}${line.content?.length > 40 ? '...' : ''}", isCustom: ${line.isCustom}, isButtonGroup: ${line.isButtonGroup}, isButton: ${line.isButton}`);
  });
  console.log('[parseTemplateForEditFirstLoadOnly] 📄 ===== END =====');
  console.log('==================================================');

  return result;
};